import {
  Box,
  type BoxProps,
  Button,
  Flex,
  HStack,
  Image,
  Link,
  Portal,
  Text,
} from '@chakra-ui/react';
import { LAUNCH_TOKEN_FEE_AGENTFUN } from '@cronos-app/sdk';
import { CloseButton } from '@ui/components/CloseButton';
import { DialogOrDrawer } from '@ui/components/DialogOrDrawer';
import { MaskIcon } from '@ui/components/MaskIcon';
import { EXTERNAL_LINK } from '@ui/config/externalLinks';
import { useAgentFunBalance } from '@ui/hooks/contracts/useTokenBalance';
import { useGlobalChainLayer } from '@ui/hooks/useGlobalChainLayer';
import { type FC } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';

import { LazyComponent } from '../LazyComponent';
import { StepsItem, StepsList, StepsRoot } from '../steps';
import { createAgentDefaultValues } from './constants';
import { CreateAgentFooter } from './CreateAgentFooter';
import { CreateAgentBasicInfo } from './Form/CreateAgentBasicInfo';
import CreateAgentExtraInfo from './Form/CreateAgentExtraInfo';
import CreateAgentPurchase from './Form/CreateAgentPurchase';
import { useCreateAgentModal } from './useCreateAgentModal';

export const CreateAgentModal = () => {
  const isCreateModalOpen = useCreateAgentModal((s) => s.isOpen);

  return (
    <LazyComponent isOpen={isCreateModalOpen}>
      <CreateAgentModalContainer>
        <DialogOrDrawer.Body pt="0px">
          <NeedAgentFun mt={3} mb={6} />
          <CreateAgentBasicInfo />
          <CreateAgentExtraInfo />
          <CreateAgentPurchase />
        </DialogOrDrawer.Body>
        <DialogOrDrawer.Footer>
          <CreateAgentFooter />
        </DialogOrDrawer.Footer>
      </CreateAgentModalContainer>
    </LazyComponent>
  );
};

const NeedAgentFun = (props: BoxProps) => {
  const chainLayer = useGlobalChainLayer();
  const { data: agentfunBalance } = useAgentFunBalance();

  if (!agentfunBalance || agentfunBalance.gt(LAUNCH_TOKEN_FEE_AGENTFUN)) {
    return null;
  }

  return (
    <Box
      px={4}
      py={3}
      borderWidth="2px"
      borderColor="primary"
      boxShadow="4px 4px 0px 0px {colors.primary}"
      rounded="12px"
      bgColor="background.rose"
      {...props}
    >
      <HStack justifyContent="space-between" textStyle="body2Bold" mb={1}>
        <Flex gap={0.5} align="center">
          Not enough
          <Image
            alt="Agent FUN"
            src="/tokens/agentfun.svg"
            boxSize="16px"
          />{' '}
          AGENTFUN!
        </Flex>
        <Link
          color="primary"
          href={EXTERNAL_LINK.swapAgentFun(chainLayer)}
          target="_blank"
        >
          Buy <MaskIcon icon="url(/masks/arrow-outward.svg)" boxSize="16px" />
        </Link>
      </HStack>
      <Text textStyle="body3">
        To create an agent you will need at least {LAUNCH_TOKEN_FEE_AGENTFUN}{' '}
        $AGENTFUN
      </Text>
    </Box>
  );
};

const Steps = () => {
  const {
    formState: { isValid: isFormValid },
  } = useFormContext();
  const stepIndex = useCreateAgentModal((s) => s.stepIndex);
  const setStep = useCreateAgentModal((s) => s.setStep);

  return (
    <StepsRoot step={stepIndex} defaultValue={0} count={3} variant="stepper">
      <StepsList mb={3}>
        <StepsItem
          onClick={() => setStep('BasicInfo')}
          index={0}
          title="Basic Info"
        />
        <StepsItem
          onClick={() => isFormValid && setStep('ExtraInfo')}
          index={1}
          title="Extra Info"
        />
        <StepsItem
          onClick={() => isFormValid && setStep('Purchase')}
          index={2}
          title="Initial Buy"
        />
      </StepsList>
    </StepsRoot>
  );
};

const CreateAgentModalContainer: FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const isOpen = useCreateAgentModal((s) => s.isOpen);
  const closeModal = useCreateAgentModal((s) => s.closeModal);
  const methods = useForm({
    defaultValues: createAgentDefaultValues,
    mode: 'onChange',
  });
  const closeAndReset = () => {
    closeModal();
    methods.reset();
  };

  return (
    <DialogOrDrawer.Root
      size={{
        desktopDown: 'full',
        desktop: 'md',
      }}
      placement={{
        desktopDown: 'bottom',
        desktop: 'top',
      }}
      scrollBehavior="inside"
      closeOnInteractOutside={false}
      open={isOpen}
      onOpenChange={(state) => !state.open && closeAndReset()}
    >
      <Portal>
        <FormProvider {...methods}>
          <DialogOrDrawer.Backdrop />
          <DialogOrDrawer.Positioner>
            <DialogOrDrawer.DragContent>
              <DialogOrDrawer.Header flexDir="column">
                <Text mb="12px">Create new agent</Text>
                <Steps />
              </DialogOrDrawer.Header>
              <DialogOrDrawer.CloseTrigger asChild>
                <CloseButton />
              </DialogOrDrawer.CloseTrigger>
              <PrevStepButton />
              {children}
            </DialogOrDrawer.DragContent>
          </DialogOrDrawer.Positioner>
        </FormProvider>
      </Portal>
    </DialogOrDrawer.Root>
  );
};

const PrevStepButton: FC = () => {
  const step = useCreateAgentModal((s) => s.step);
  const setPrevStep = useCreateAgentModal((s) => s.setPrevStep);
  if (step === 'BasicInfo') return null;

  return (
    <Button
      variant="icon"
      position="absolute"
      top="16px"
      insetStart="20px"
      onClick={(e) => {
        setPrevStep();
        e.stopPropagation();
      }}
    >
      <MaskIcon
        icon="url(/masks/chevron.svg)"
        bgColor="currentcolor"
        boxSize="24px"
        transform="rotate(90deg)"
      />
    </Button>
  );
};
